import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import * as moment from "moment-timezone";
import { Config } from "src/app/_store/config";

import {
  ChangeRequestFieldMeta,
  CHANGE_REQUEST_FIELD_METADATA,
  ChangeRequestValidationRule,
} from "./change-request-field-metadata";

const PAN_REGEX = /^[A-Z]{3}[PCHFATBLJG][A-Z][0-9]{4}[A-Z]$/i;
const GST_REGEX =
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/i;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/i;

/** True when value matches the IFSC field validator (empty is invalid for fetch). */
export function isValidIfsc(value: unknown): boolean {
  const v = String(value ?? "").trim();
  return v.length > 0 && IFSC_REGEX.test(v);
}
const INDIA_PIN_REGEX = /^[0-9]{6}$/;
const EMAIL_REGEX =
  /^(NA|na|Na|nA|[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+)$/;
const MOBILE_INDIA_REGEX = /^[0-9]{10}$/;

/** API / session keys that share metadata with another form key. */
const FORM_KEY_ALIASES: Record<string, string> = {
  BANKL: "IFSC_CODE",
  PAN: "PAN_NUMBER",
  ERP_NO: "SAP_VENDOR_CODE",
};

function resolveFormKey(formKey: string): string {
  return FORM_KEY_ALIASES[formKey] ?? formKey;
}

export function getFieldMeta(formKey: string): ChangeRequestFieldMeta | undefined {
  return CHANGE_REQUEST_FIELD_METADATA[resolveFormKey(formKey)];
}

export function isFieldEditable(
  formKey: string,
  sectionId: "requestor" | "vendor",
): boolean {
  if (sectionId === "requestor") {
    return false;
  }
  const meta = getFieldMeta(formKey);
  if (!meta) {
    return false;
  }
  return meta.editable;
}

export function fieldPortalLabel(formKey: string, fallback: string): string {
  return getFieldMeta(formKey)?.portalLabel ?? fallback;
}

export function fieldSupportingDocuments(formKey: string): string[] {
  return getFieldMeta(formKey)?.supportingDocuments ?? [];
}

export function fieldValidationHint(formKey: string): string | undefined {
  return getFieldMeta(formKey)?.validationHint;
}

function validatorForRule(rule: ChangeRequestValidationRule | undefined): ValidatorFn | null {
  switch (rule) {
    case "numeric":
      return (c: AbstractControl): ValidationErrors | null => {
        const v = String(c.value ?? "").trim();
        if (!v) return null;
        return /^[0-9+\-\s]+$/.test(v) ? null : { numeric: true };
      };
    case "email":
      return (c: AbstractControl): ValidationErrors | null => {
        const v = String(c.value ?? "").trim();
        if (!v) return null;
        return EMAIL_REGEX.test(v) ? null : { email: true };
      };
    case "pan":
      return (c: AbstractControl): ValidationErrors | null => {
        const v = String(c.value ?? "").trim().toUpperCase();
        if (!v) return null;
        return PAN_REGEX.test(v) ? null : { pan: true };
      };
    case "gst":
      return (c: AbstractControl): ValidationErrors | null => {
        const v = String(c.value ?? "").trim().toUpperCase();
        if (!v) return null;
        return GST_REGEX.test(v) ? null : { gst: true };
      };
    case "ifsc":
      return (c: AbstractControl): ValidationErrors | null => {
        const v = String(c.value ?? "").trim().toUpperCase();
        if (!v) return null;
        return IFSC_REGEX.test(v) ? null : { ifsc: true };
      };
    case "indiaPin":
      return (c: AbstractControl): ValidationErrors | null => {
        const v = String(c.value ?? "").trim();
        if (!v) return null;
        return INDIA_PIN_REGEX.test(v) ? null : { indiaPin: true };
      };
    case "mobileIndia":
      return (c: AbstractControl): ValidationErrors | null => {
        const v = String(c.value ?? "").trim();
        if (!v) return null;
        return MOBILE_INDIA_REGEX.test(v) ? null : { mobile: true };
      };
    case "date":
      return (c: AbstractControl): ValidationErrors | null => {
        const v = c.value;
        if (v === null || v === undefined || v === "") {
          return null;
        }
        if (isNgbDateStruct(v)) {
          return null;
        }
        const s = String(v).trim();
        if (!s) {
          return null;
        }
        return parseChangeRequestDate(s) ? null : { date: true };
      };
    case "freeText":
    case "displayOnly":
    case "none":
    default:
      return null;
  }
}

function isNgbDateStruct(v: unknown): v is { year: number; month: number; day: number } {
  return (
    !!v &&
    typeof v === "object" &&
    typeof (v as { year?: unknown }).year === "number" &&
    typeof (v as { month?: unknown }).month === "number" &&
    typeof (v as { day?: unknown }).day === "number"
  );
}

/** Parse portal / session date strings to moment (valid formats only). */
export function parseChangeRequestDate(value: string): moment.Moment | null {
  const s = (value || "").trim();
  if (!s) {
    return null;
  }
  const m = moment(
    s,
    [
      Config.dateSaveFormat,
      Config.dateDisplayFormat,
      "YYYY-MM-DD",
      "DD-MMM-YYYY",
      "DD/MM/YYYY",
    ],
    true,
  );
  return m.isValid() ? m : null;
}

export function formatChangeRequestDateValue(value: unknown): string {
  if (isNgbDateStruct(value)) {
    const month = String(value.month).padStart(2, "0");
    const day = String(value.day).padStart(2, "0");
    return `${value.year}-${month}-${day}`;
  }
  const s = String(value ?? "").trim();
  if (!s) {
    return "";
  }
  const m = parseChangeRequestDate(s);
  return m ? m.format(Config.dateSaveFormat) : s;
}

export function isBankFieldRequired(bankKey: string): boolean {
  return getFieldMeta(bankKey)?.required === true;
}

export function buildValidators(meta: ChangeRequestFieldMeta | undefined): ValidatorFn[] {
  const validators: ValidatorFn[] = [];
  if (!meta) {
    return validators;
  }
  if (meta.required) {
    validators.push(Validators.required);
  }
  if (meta.maxLength != null && meta.maxLength > 0) {
    validators.push(Validators.maxLength(meta.maxLength));
  }
  const ruleValidator = validatorForRule(meta.validationRule);
  if (ruleValidator) {
    validators.push(ruleValidator);
  }
  return validators;
}

export function isControlEditableForDiff(
  formKey: string,
  sectionId: "requestor" | "vendor" | undefined,
): boolean {
  if (sectionId === "requestor") {
    return false;
  }
  return isFieldEditable(formKey, "vendor");
}

export function isBankKeyEditable(bankKey: string): boolean {
  return isFieldEditable(bankKey, "vendor");
}

/** User-facing message for the first validation error on a control. */
export function getValidationErrorMessage(
  errors: ValidationErrors | null | undefined,
): string | null {
  if (!errors) {
    return null;
  }
  if (errors["required"]) {
    return "This field is required.";
  }
  if (errors["maxlength"]) {
    const req = errors["maxlength"].requiredLength;
    return `Maximum ${req} characters allowed.`;
  }
  if (errors["email"]) {
    return "Invalid email format.";
  }
  if (errors["pan"]) {
    return "Invalid PAN format.";
  }
  if (errors["gst"]) {
    return "Invalid GST number format.";
  }
  if (errors["ifsc"]) {
    return "Invalid IFSC / bank key format.";
  }
  if (errors["indiaPin"]) {
    return "Invalid postal code (6 digits for India).";
  }
  if (errors["mobile"]) {
    return "Invalid mobile number (10 digits for India).";
  }
  if (errors["numeric"]) {
    return "Only numbers and valid phone characters are allowed.";
  }
  if (errors["date"]) {
    return "Enter a valid date.";
  }
  return "Invalid value.";
}
